import { getTotalPrice } from '@shared/lib/money';
import * as React from 'react';
import { useCallback, useContext, useMemo } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import MediaQuery, { useMediaQuery } from 'react-responsive';
import { Settings } from 'react-slick';

import { PassengerState } from '../../../Checkout/store/passengers/reducers';
import { useTheme } from '../../../theme';
import { default as BaseButton } from '../../../BaseComponents/Button';
import { Money } from '../../../Money';
import SeatMapDisclaimer from '../SeatMapDisclaimer/SeatMapDisclaimer';
import { Seat, Segment, ServicesMap } from '../../types';
import Passenger from './Passenger/Passenger';
import Controls from '../SeatMapService/Controls/Controls';
import { SeatsRecommendContext } from '../../../Checkout/components/Checkout/SeatsRecommendation/SeatsRecommendation';
import Notification from '../../../Notification';
import { InfoMessageTypeEnum, AviaPassengerType, SeatMapRestrictions } from '@websky/graphql';
import { MoneyType } from '../../../PriceMatrix/types';
import { TABLET_MIDDLE_WIDTH } from '../../../utils';
import {
	getAvailableSeatsCount,
	getEffectivePassengerFilters,
	getLinkedPassengerTypes,
	getSeatPrice,
	filterNotInfantSeatRestrictions
} from '../../utils';
import { EMPTY_PASSENGER_FILTERS_DATA } from '../../constants';
import { PassengerFiltersContext } from '../../types';
import Slider from './Slider/Slider';
import { SafeHtml } from '@shared/ui';

type SeatMapDisclaimerWrapperProps = {
	hideDisclaimer?: boolean;
};

const SeatMapDisclaimerWrapper: React.FC<SeatMapDisclaimerWrapperProps> = React.memo(({ hideDisclaimer }) => {
	const { t, i18n } = useTranslation('SeatMap');
	const { PassengersStyles: theme } = useTheme('SeatMap');

	const strings = [
		'Pay attention!',
		'The airline reserves the right to replace the aircraft.',
		'In the event of an aircraft replacement, passengers who have paid for the “seat selection” service will be provided with seats on the plane that are similar in price and comfort, while maintaining the following parameters',
		'- Cost of services;',
		'- Location of the place relative to the window / aisle;',
		'- Joint seating, if one was originally selected;',
		'- Placement in the front row of the cabin, if one was originally selected.'
	];

	if (hideDisclaimer) {
		return null;
	}

	return (
		<SeatMapDisclaimer>
			{i18n.exists('SeatMap:Disclaimer') ? (
				<SafeHtml tag="span" className={theme.disclaimer} html={t('SeatMap:Disclaimer')} />
			) : (
				<>
					{strings.map(label => (
						<SafeHtml key={label} tag="p" html={t(label)} />
					))}
				</>
			)}
		</SeatMapDisclaimer>
	);
});

interface RenderersProps {
	renderDoubleSeats?: () => React.ReactNode;
	renderFooterBefore?: () => React.ReactNode;
}

interface OwnProps {
	hideDisclaimer?: boolean;
	hideNextButton?: boolean;
	hidePassengers?: boolean;
	services: ServicesMap;
	passengers: PassengerState[];
	segments: Segment[];
	segmentId: string;
	passengerId: string;
	forceGoToSliderStart?: number;
	segmentIndex: number;
	seatMapRef?: React.RefObject<HTMLDivElement>;
	hasSeatsWithAnimals?: boolean;
	onProceed: () => void;
	onChange: (passengerId: string) => void;
	onCancel: (passengerId: string) => void;
	onClear: () => void;
	onSelectNextFlight: () => void;
	toggleExtraSeat?: (active: boolean) => void;
	hasSelectedExtraSeats?: boolean;
	passengersIdsWithExtraSeats?: string[];
	isNewOrder?: boolean;
	isDialog?: boolean;
	activeFilters?: SeatMapRestrictions[];
	passengerFiltersContext?: PassengerFiltersContext;
}

type Props = OwnProps & RenderersProps;

export const filledPersonIcon = (
	<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M18.666 9.33335C18.666 11.9117 16.5777 14 13.9993 14C11.421 14 9.33268 11.9117 9.33268 9.33335C9.33268 6.75502 11.421 4.66669 13.9993 4.66669C16.5777 4.66669 18.666 6.75502 18.666 9.33335ZM4.66602 19.8334C4.66602 16.73 10.8843 15.1667 13.9993 15.1667C17.1143 15.1667 23.3327 16.73 23.3327 19.8334V23.3334H4.66602V19.8334Z"
			fill="currentColor"
		/>
	</svg>
);

export const recommend = (
	<svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M17.9999 6.66683C18.9166 6.66683 19.6666 7.41683 19.6666 8.3335L19.6583 8.40016L19.6666 8.4085V10.0002C19.6666 10.2168 19.6249 10.4168 19.5499 10.6085L17.0333 16.4835C16.7833 17.0835 16.1916 17.5002 15.4999 17.5002H7.99992C7.08325 17.5002 6.33325 16.7502 6.33325 15.8335V7.50016C6.33325 7.04183 6.51658 6.62516 6.82492 6.32516L12.3083 0.833496L13.1916 1.7085C13.4166 1.9335 13.5583 2.25016 13.5583 2.59183L13.5333 2.8585L12.7416 6.66683H17.9999ZM4.66658 17.5002H1.33325V7.50016H4.66658V17.5002Z"
			fill="currentColor"
		/>
	</svg>
);

const Passengers: React.FunctionComponent<Props> = props => {
	const { PassengersStyles: theme, ComponentStyles: seatMapTheme } = useTheme('SeatMap');
	const { t } = useTranslation('SeatMap');

	const segmentsCount = props.segments.length;

	const segmentSeats = useMemo(() => {
		const rows = props.segments[props.segmentIndex]?.decks?.[0]?.rows;

		if (!rows?.length) {
			return [];
		}

		return rows.reduce<Seat[]>((acc, row) => [...acc, ...row.seats], []);
	}, [props.segments, props.segmentIndex]);

	const getPassengerFiltersData = useCallback(
		(passengerId: string) => props.passengerFiltersContext?.get(passengerId) ?? EMPTY_PASSENGER_FILTERS_DATA,
		[props.passengerFiltersContext]
	);

	const afterChange = useCallback(
		(currentSlide: number) => {
			return props.onChange(props.passengers[Math.ceil(currentSlide)].id);
		},
		[props.passengers, props.onChange]
	);

	const sliderSettings: Settings = {
		className: theme.slider,
		slidesToShow: 1,
		infinite: false,
		centerPadding: '20px',
		centerMode: true,
		arrows: false,
		dots: false,
		afterChange
	};

	const seatRecommendContext = useContext(SeatsRecommendContext);

	const isTablet = useMediaQuery({ maxWidth: TABLET_MIDDLE_WIDTH });
	const isDesktopRecommendationOpen = !isTablet && seatRecommendContext.isOpen; // only for desktop

	const hasRecommendSeats =
		seatRecommendContext?.rowsMap &&
		Object.keys(seatRecommendContext?.rowsMap).length > 0 &&
		seatRecommendContext.availableSeatsCount > 0;

	const hasRemovableSeats = props.passengers.some(passenger =>
		passenger.seats?.some(selectedSeat => !selectedSeat?.isConfirmed && selectedSeat.segmentId === props.segmentId)
	);

	const filterSeatMapFilters = React.useCallback(
		(passenger: PassengerState) => {
			const {
				PregnantWomen,
				PassengersUnder18,
				Infant,
				Unaccompanied,
				WithAnimals,
				ElderlyPerson
			} = SeatMapRestrictions;

			const childRestrictionsToExclude = [PregnantWomen, PassengersUnder18, Infant, ElderlyPerson];
			const infantRestrictionsToExclude = [...childRestrictionsToExclude, Unaccompanied, WithAnimals];

			const { allowedFilters, availableSeats } = getPassengerFiltersData(passenger.id);

			let passengerFilters = allowedFilters;
			const hasLinkedINS = getLinkedPassengerTypes(passenger, props.passengers).has(AviaPassengerType.INS);

			if (passenger.type === AviaPassengerType.CLD) {
				passengerFilters = passengerFilters.filter(filter => !childRestrictionsToExclude.includes(filter));
			}

			if (passenger.type === AviaPassengerType.INS) {
				passengerFilters = passengerFilters.filter(filter => !infantRestrictionsToExclude.includes(filter));
			}

			if (hasLinkedINS) {
				passengerFilters = passengerFilters.filter(filterNotInfantSeatRestrictions);
			}

			return getEffectivePassengerFilters(availableSeats, passengerFilters, props.activeFilters);
		},
		[getPassengerFiltersData, props.activeFilters, props.passengers]
	);

	const renderPassengersForRecommend = () => {
		const rowsMap = seatRecommendContext.rowsMap;

		return (
			<>
				<div className={theme.recommend__wrapper}>
					<div className={theme.recommend__label}>
						{recommend}

						{t('Recommend')}
					</div>

					{props.passengers.length > 1 && <div className={theme.together}>{t('Fly together!')}</div>}

					<div>
						{props.passengers.map(passenger => {
							const selectedSeat = rowsMap[props.segmentId]?.seats?.[passenger.id];
							if (!selectedSeat) {
								return null;
							}

							return (
								<Passenger
									passenger={passenger}
									key={passenger.id}
									isActive={passenger.id === props.passengerId}
									seats={[selectedSeat.seat]}
									seatsPrice={selectedSeat.seat.price}
									segmentsCount={segmentsCount}
									onClick={props.onChange}
									onClear={props.onCancel}
									recommend={true}
									isNewOrder={props.isNewOrder}
								/>
							);
						})}
					</div>

					<div className={theme.recommend__price}>
						{t('Total')}: <Money money={rowsMap[props.segmentId]?.price} />
					</div>
				</div>

				<div className={theme.passengers__buttons}>
					<BaseButton
						variant="smallSecondary"
						data-test-id="recommend-cancel"
						onClick={seatRecommendContext.onDisagree}
						classes={{ root: cn(theme.passengers__button, theme.passengers__button_clear) }}
					>
						{t('Choose myself')}
					</BaseButton>

					<BaseButton
						variant="smallPrimary"
						data-test-id="recommend-confirm"
						classes={{
							root: cn(theme.passengers__button)
						}}
						onClick={seatRecommendContext.onConfirm}
					>
						{t('Confirm')}
					</BaseButton>
				</div>
			</>
		);
	};

	const renderPassengersList = () => {
		return props.passengers.map(passenger => {
			const seatPrices: MoneyType[] = [];

			const selectedSeats = passenger.seats?.filter(seat => seat.segmentId === props.segmentId) || [];

			for (const seat of selectedSeats) {
				const actualSeat = seat.seatServices?.length ? seat : segmentSeats.find(s => s.number === seat.number);

				if (!actualSeat) {
					continue;
				}

				const seatPrice = getSeatPrice(actualSeat, passenger.id);

				seat.price = seatPrice;
				seatPrices.push(seatPrice);
			}

			const selectedSeatsPrice = getTotalPrice(seatPrices, price => price);

			const passengerSeatMapFilters = filterSeatMapFilters(passenger);
			const { availableSeats: passengerAvailableSeats } = getPassengerFiltersData(passenger.id);
			const passengerFilteredSeatsCount = getAvailableSeatsCount(passengerAvailableSeats, props.activeFilters);

			return (
				<Passenger
					passenger={passenger}
					key={passenger.id}
					isActive={passenger.id === props.passengerId}
					isActiveDoubleSeat={props.passengersIdsWithExtraSeats?.includes(passenger.id)}
					seats={selectedSeats}
					seatsPrice={selectedSeatsPrice}
					segmentsCount={segmentsCount}
					onClick={props.onChange}
					onClear={props.onCancel}
					isNewOrder={props.isNewOrder}
					toggleExtraSeat={props.toggleExtraSeat}
					allowedFilters={passengerSeatMapFilters}
					activeFilters={props.activeFilters}
					filteredSeatsCount={passengerFilteredSeatsCount}
				/>
			);
		});
	};

	const renderPassengers = () => {
		if (props.hidePassengers) {
			return null;
		}

		return (
			<>
				{renderPassengersList()}

				{props.renderDoubleSeats && (
					<div className={theme.passengers__doubleSeat}>{props.renderDoubleSeats()}</div>
				)}

				{props.renderFooterBefore?.()}

				<div className={theme.passengers__footer}>
					{seatRecommendContext.isEnabled && hasRecommendSeats && !props.hasSelectedExtraSeats && (
						<div className={theme.recommend} onClick={seatRecommendContext.onOpen}>
							<div className={theme.recommend__icon}>{recommend}</div>
							{t('Show recommended seats')}
						</div>
					)}

					<Controls
						segmentId={props.segmentIndex}
						segmentsCount={segmentsCount}
						onClear={props.onClear}
						onProceed={props.onProceed}
						clearEnable={hasRemovableSeats}
						onSelectNextFlight={props.onSelectNextFlight}
					/>

					{props.hasSeatsWithAnimals && (
						<div className={theme.notifications}>
							<Notification
								className={theme.notification}
								status={InfoMessageTypeEnum.Promo}
								type="context"
								infoMessage={{
									text: t(
										'The passenger can select a suitable seat marked for passengers with pets on the map. The airline has the right to transfer a passenger with an animal.'
									),
									type: InfoMessageTypeEnum.Promo,
									code: 'pet-disclaimer'
								}}
							/>
						</div>
					)}

					<SeatMapDisclaimerWrapper />
				</div>
			</>
		);
	};

	return (
		<div
			id="passengers"
			className={cn(theme.passengers, {
				[theme.passengers_recommend]: seatRecommendContext.isOpen,
				[seatMapTheme.passengers_dialog]: props.isDialog
			})}
		>
			{isDesktopRecommendationOpen ? (
				renderPassengersForRecommend()
			) : (
				<>
					<MediaQuery minWidth={TABLET_MIDDLE_WIDTH + 1}>{renderPassengers()}</MediaQuery>
					<MediaQuery maxWidth={TABLET_MIDDLE_WIDTH}>
						<Slider
							passengers={props.passengers}
							segmentId={props.segmentId}
							passengerId={props.passengerId}
							settings={sliderSettings}
							forceGoToSliderStart={props.forceGoToSliderStart}
						>
							{renderPassengersList()}
						</Slider>
					</MediaQuery>
				</>
			)}
		</div>
	);
};

export default Passengers;
